/**
 * useMachines — Fetch machines with 30-second auto-refresh.
 */

import { useState, useEffect, useCallback } from 'react';
import { getMachines, type Machine } from '../api/client';

const REFRESH_INTERVAL_MS = 30_000;

interface UseMachinesResult {
  machines: Machine[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useMachines(): UseMachinesResult {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await getMachines();
      setMachines(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch machines');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [refresh]);

  return { machines, loading, error, refresh };
}
